import { z } from "zod";
import type { PartnerFlow } from "@remi/enums/partner-flow";
import { entries } from "@remi/ts-utils";

import type { BuyerId } from "./buyer";
import {
  AMERICAN_FAMILY_ORG,
  ARCASA_ORG,
  BRANCH_PARTNER_ORG,
  BREEZY_ORG,
  DOMBOL_ORG,
  FIELD_SALES_ORG,
  OPEN_DOOR_ORG,
  OPENLY_ORG,
  REFERRAL_ORG,
  REMI_WEBSITE_ORG,
  SUNRUN_ORG,
  THUMBTACK_ORG,
  TRUCOST_ORG,
  WESTHILL_ORG,
} from "./organizations";

export const SourceValues = [
  "PRE_PTO",
  "POST_PTO",
  "INSURANCE",
  "DIRECT_TO_CONSUMER",
  "LOAN_ORIGINATOR",
  "DEALER",
  "REAL_ESTATE",
  "PILOT",
  "REFERRAL",
] as const;
export const SourceSchema = z.enum(SourceValues);
export const Source = SourceSchema.enum;
export type Source = z.infer<typeof SourceSchema>;
export const SourceDisplayValues: Record<Source, string> = {
  PRE_PTO: "Pre-PTO",
  POST_PTO: "Post-PTO",
  INSURANCE: "Insurance",
  DIRECT_TO_CONSUMER: "In App",
  LOAN_ORIGINATOR: "Loan Originator",
  DEALER: "Dealer",
  REAL_ESTATE: "Real Estate",
  PILOT: "Pilot",
  REFERRAL: "Referral",
};
export const PartnerFlowToBuyerOrganizationId: Record<PartnerFlow, [BuyerId, ...BuyerId[]]> = {
  SUNRUN_PRE_PTO: [SUNRUN_ORG.id],
  SUNRUN_PRE_PTO_STRATEGIC_PARTNER: [SUNRUN_ORG.id],
  SUNRUN_POST_PTO_DAMAGE: [SUNRUN_ORG.id],
  SUNRUN_POST_PTO_RNR: [SUNRUN_ORG.id],
  SUNRUN_POST_PTO_WORK_ORDER: [SUNRUN_ORG.id],
  SUNRUN_POST_PTO_WORK_ORDER_ONLY: [SUNRUN_ORG.id],
  BRANCH_INSURANCE: [BRANCH_PARTNER_ORG.id],
  DIRECT_TO_CONSUMER: [DOMBOL_ORG.id, REMI_WEBSITE_ORG.id, REFERRAL_ORG.id],
  DIRECT_TO_CONSUMER_RANGED_QUOTE: [DOMBOL_ORG.id, REMI_WEBSITE_ORG.id, REFERRAL_ORG.id],
  FIELD_SALES: [FIELD_SALES_ORG.id],
  ARCASA: [ARCASA_ORG.id],
  AMERICAN_FAMILY: [AMERICAN_FAMILY_ORG.id],
  AMERICAN_FAMILY_ESTIMATE_ONLY: [AMERICAN_FAMILY_ORG.id],
  AMERICAN_FAMILY_INSURANCE: [AMERICAN_FAMILY_ORG.id],
  WESTHILL: [WESTHILL_ORG.id],
  TRUCOST: [TRUCOST_ORG.id],
  BREEZY: [BREEZY_ORG.id],
  OPENLY: [OPENLY_ORG.id],
  OPEN_DOOR: [OPEN_DOOR_ORG.id],
  WESTHILL_MRP: [WESTHILL_ORG.id],
  WESTHILL_STATE_FARM: [WESTHILL_ORG.id],
  THUMBTACK: [THUMBTACK_ORG.id],
  // Warranty bids are Remi-internal corrections work. They originate from the Create Corrections
  // drawer on an existing bid, which carries the original buyer org via its workSite. The warranty
  // bid's own BuyerId is best-effort — REMI_WEBSITE for generic warranty, SUNRUN for Sunrun warranty.
  WARRANTY: [REMI_WEBSITE_ORG.id],
  SUNRUN_REMI_WARRANTY: [SUNRUN_ORG.id],
};

// @ts-expect-error - we populate this after instantiating the object
const _BuyerIdToPartnerFlow: Record<BuyerId, [PartnerFlow, ...PartnerFlow[]]> = {};
for (const [partnerFlow, buyerOrganizationIds] of entries(PartnerFlowToBuyerOrganizationId)) {
  for (const buyerOrganizationId of buyerOrganizationIds) {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (!_BuyerIdToPartnerFlow[buyerOrganizationId]) {
      _BuyerIdToPartnerFlow[buyerOrganizationId] = [partnerFlow];
    } else {
      _BuyerIdToPartnerFlow[buyerOrganizationId].push(partnerFlow);
    }
  }
}
export const BuyerIdToPartnerFlow = _BuyerIdToPartnerFlow;

export const PartnerFlowToSource: Record<PartnerFlow, Source> = {
  SUNRUN_PRE_PTO: "PRE_PTO",
  SUNRUN_PRE_PTO_STRATEGIC_PARTNER: "PRE_PTO",
  SUNRUN_POST_PTO_DAMAGE: "POST_PTO",
  SUNRUN_POST_PTO_RNR: "POST_PTO",
  SUNRUN_POST_PTO_WORK_ORDER: "POST_PTO",
  SUNRUN_POST_PTO_WORK_ORDER_ONLY: "POST_PTO",
  BRANCH_INSURANCE: "INSURANCE",
  DIRECT_TO_CONSUMER: "DIRECT_TO_CONSUMER",
  DIRECT_TO_CONSUMER_RANGED_QUOTE: "DIRECT_TO_CONSUMER",
  FIELD_SALES: "DIRECT_TO_CONSUMER",
  ARCASA: "LOAN_ORIGINATOR",
  AMERICAN_FAMILY: "INSURANCE",
  AMERICAN_FAMILY_ESTIMATE_ONLY: "INSURANCE",
  AMERICAN_FAMILY_INSURANCE: "INSURANCE",
  WESTHILL: "INSURANCE",
  WESTHILL_MRP: "INSURANCE",
  WESTHILL_STATE_FARM: "INSURANCE",
  TRUCOST: "DEALER",
  BREEZY: "DEALER",
  OPENLY: "INSURANCE",
  OPEN_DOOR: "REAL_ESTATE",
  THUMBTACK: "INSURANCE",
  // `Source` flows through `SourceToCommissionStructure` at bid creation
  // (`packages/dao/src/bidDao.ts`'s create path). PRE_PTO yields `NO_COMMISSION`, which is the
  // intended behavior for both warranty flows (corrections-spawned work earns no sales commission).
  // Confirm with the business team before PR 4's migration runs in case generic warranty bids
  // should ever earn commission.
  WARRANTY: "PRE_PTO",
  SUNRUN_REMI_WARRANTY: "PRE_PTO",
};
